import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useMsal } from '@azure/msal-react';

export interface UserInfo {
    name?: string;
    isAdmin: boolean;
    availableLocations: string[];
    roles: string[];
}

export const useUser = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { callApi } = useApi();
    const { instance } = useMsal();

    const fetchUserInfo = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await callApi('/api/User/info');
            
            if (!response.ok) {
                throw new Error('Failed to fetch user info');
            }
            
            const info = await response.json() as UserInfo;
            setUserInfo(info);
        } catch (err) {
            console.error('Error fetching user info:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch user info');
            setUserInfo(null);
        } finally {
            setLoading(false);
        }
    }, [callApi]);

    // Fetch user info on mount
    useEffect(() => {
        if (instance.getActiveAccount()) {
            fetchUserInfo();
        }
    }, [fetchUserInfo, instance]);

    return {
        userInfo,
        loading,
        error,
        refetch: fetchUserInfo,
        isAdmin: userInfo?.isAdmin ?? false,
        availableLocations: userInfo?.availableLocations ?? [],
        roles: userInfo?.roles ?? []
    };
}; 